import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ProductCard } from '@/components/ProductCard';
import { ContactSellerButton } from '@/components/ContactSellerButton';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { useFollows } from '@/lib/follows';
import { useSEO } from '@/lib/useSEO';
import { formatDate } from '@/lib/format';
import {
  Star, MapPin, ShieldCheck, Store, Award, ShoppingBag, Package, Calendar,
  UserPlus, UserCheck, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const RATING_LABELS = { 5: 'Excelente', 4: 'Muy bueno', 3: 'Regular', 2: 'Malo', 1: 'Pésimo' };

export default function SellerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const follows = useFollows();
  const isFollowing = follows?.isFollowing?.(id);

  useEffect(() => {
    api.get(`/sellers/${id}/public-profile`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true));
  }, [id]);

  const onFollowClick = async () => {
    if (!user) {
      toast.error('Inicia sesión para seguir vendedores');
      return;
    }
    if (user.role !== 'buyer') {
      toast.error('Solo compradores pueden seguir vendedores');
      return;
    }
    const result = await follows.toggle(id);
    if (result.ok) {
      toast.success(result.following ? 'Ahora sigues a este vendedor' : 'Dejaste de seguir al vendedor');
    }
  };

  const seller = data?.seller;
  const isPro = seller?.plan === 'pro';

  useSEO(seller ? {
    title: `${seller.business_name} | Vendedor en AutoMarket Colombia`,
    description: `Perfil del vendedor ${seller.business_name}${seller.city ? ` en ${seller.city}` : ''}. ${data.stats.products_total} productos publicados, ${data.rating.count} calificaciones.`,
    canonical: `/tienda/${id}`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: seller.business_name,
      address: seller.address || seller.city,
      ...(data.rating.average ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: data.rating.average,
          reviewCount: data.rating.count,
        },
      } : {}),
    },
  } : { title: 'Vendedor — AutoMarket Colombia' });

  if (error) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <Store className="mx-auto h-12 w-12 text-slate-300" />
          <h1 className="mt-4 font-display text-2xl font-black text-slate-900">Vendedor no encontrado</h1>
          <p className="text-slate-500 mt-2">El perfil que buscas no existe o fue desactivado.</p>
          <Link to="/catalogo" className="mt-4 inline-block text-blue-700 hover:underline font-semibold">
            ← Volver al catálogo
          </Link>
        </div>
      </Layout>
    );
  }
  if (!data) {
    return <Layout><div className="p-12 text-center text-slate-500">Cargando perfil...</div></Layout>;
  }

  const { rating, stats, products, recent_reviews } = data;
  const onAdd = (p) => { add(p); toast.success(`${p.name} agregado al carrito`); };
  const totalReviews = rating.count;

  return (
    <Layout>
      {/* Cover header */}
      <section className="relative border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5 flex-wrap">
            <div className={`flex h-20 w-20 items-center justify-center text-white text-3xl font-black ${isPro ? 'bg-orange-500 text-slate-900' : 'bg-white text-slate-900'}`}>
              {seller.business_name?.[0]?.toUpperCase() || 'V'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isPro && (
                  <span className="inline-flex items-center gap-1 bg-orange-500 text-slate-900 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                    <Award className="h-3 w-3" /> PRO
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 px-2 py-1">
                  <ShieldCheck className="h-3 w-3" /> Verificado
                </span>
              </div>
              <h1 data-testid="seller-profile-name" className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
                {seller.business_name}
              </h1>
              <div className="mt-2 text-sm text-slate-300 flex flex-wrap items-center gap-x-4 gap-y-1">
                {seller.city && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {seller.city}
                  </span>
                )}
                {seller.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" /> Miembro desde {new Date(seller.created_at).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}
                  </span>
                )}
                {rating.average != null && (
                  <span className="inline-flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 fill-orange-400 text-orange-400" />
                    <span className="font-bold text-white">{rating.average}</span>
                    <span className="text-slate-400">({rating.count} reseñas)</span>
                  </span>
                )}
              </div>
            </div>
            {seller.user_id && (
              <div className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-5 h-11 border-2 border-white">
                <ContactSellerButton
                  sellerId={seller.user_id}
                  sellerName={seller.business_name}
                  dataTestId="seller-profile-contact-btn"
                />
              </div>
            )}
            <button
              onClick={onFollowClick}
              data-testid="seller-follow-btn"
              className={`inline-flex items-center gap-2 px-5 h-11 font-bold border-2 transition-colors ${
                isFollowing
                  ? 'bg-white border-slate-900 text-slate-900 hover:bg-slate-100'
                  : 'bg-transparent border-orange-400 text-orange-300 hover:bg-orange-400 hover:text-slate-900'
              }`}
            >
              {isFollowing ? (
                <><UserCheck className="h-4 w-4" /> Siguiendo</>
              ) : (
                <><UserPlus className="h-4 w-4" /> Seguir</>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Package} label="Productos publicados" value={stats.products_total} testId="seller-stat-products" />
          <Stat icon={ShoppingBag} label="Ventas completadas" value={stats.sales_done} testId="seller-stat-sales" />
          <Stat
            icon={Star}
            label="Calificación promedio"
            value={rating.average != null ? `${rating.average} / 5` : 'Sin calificaciones'}
            testId="seller-stat-rating"
            sub={`${rating.count} reseñas`}
          />
          <Stat icon={Users} label="Seguidores" value={stats.followers || 0} testId="seller-stat-followers" />
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-10">
        {/* Products */}
        <div>
          <h2 className="font-display text-2xl font-black tracking-tighter text-slate-900 mb-4">
            Productos del vendedor
          </h2>
          {products.length === 0 ? (
            <p className="text-slate-500 text-sm">Este vendedor aún no tiene productos activos.</p>
          ) : (
            <div data-testid="seller-products-grid" className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {products.map((p) => (
                <ProductCard
                  key={p.product_id}
                  product={{ ...p, seller_name: seller.business_name, seller_city: seller.city, seller_phone: seller.phone }}
                  onAdd={onAdd}
                />
              ))}
            </div>
          )}
          {stats.products_total > products.length && (
            <div className="mt-6 text-center">
              <Link
                to={`/catalogo?seller_id=${id}`}
                className="inline-block text-blue-700 hover:underline font-semibold"
              >
                Ver los {stats.products_total} productos →
              </Link>
            </div>
          )}
        </div>

        {/* Reputation sidebar */}
        <aside className="space-y-6">
          {/* Rating distribution */}
          <div className="border border-slate-200 bg-white p-5">
            <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">
              Distribución de calificaciones
            </h3>
            {totalReviews === 0 ? (
              <p className="text-xs text-slate-500">Aún sin calificaciones recibidas.</p>
            ) : (
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((s) => {
                  const count = rating.breakdown[s] || 0;
                  const pct = totalReviews ? Math.round((count / totalReviews) * 100) : 0;
                  return (
                    <div key={s} className="flex items-center gap-2 text-xs" data-testid={`rating-row-${s}`}>
                      <span className="w-10 text-slate-700 font-semibold">{s} ★</span>
                      <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-slate-600">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent reviews */}
          <div className="border border-slate-200 bg-white p-5">
            <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">
              Reseñas recientes
            </h3>
            {recent_reviews.length === 0 ? (
              <p className="text-xs text-slate-500">Las reseñas aparecerán aquí cuando los compradores califiquen.</p>
            ) : (
              <ul className="space-y-4">
                {recent_reviews.map((r) => (
                  <li key={r.review_id} className="border-b border-slate-100 pb-3 last:border-0" data-testid={`review-${r.review_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-900 text-sm">{r.buyer_name}</span>
                      <span className="inline-flex items-center gap-0.5 text-orange-500">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-current" />
                        ))}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      {RATING_LABELS[r.rating]} · {formatDate(r.created_at)}
                    </div>
                    {r.comment && <p className="text-xs text-slate-700 mt-1">{r.comment}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </Layout>
  );
}

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="flex items-start gap-3">
    <div className="flex h-10 w-10 items-center justify-center bg-slate-900 text-white">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display text-2xl font-black text-slate-900 leading-none mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  </div>
);
